import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import OnboardingShell from "@/components/onboarding/OnboardingShell";
import StepWelcome from "@/components/onboarding/StepWelcome";
import Step1AboutYou from "@/components/onboarding/Step1AboutYou";
import Step2Goal from "@/components/onboarding/Step2Goal";
import Step3Body from "@/components/onboarding/Step3Body";
import Step4Training from "@/components/onboarding/Step4Training";
import Step5Nutrition from "@/components/onboarding/Step5Nutrition";
import Step6Recovery from "@/components/onboarding/Step6Recovery";
import Step7Health from "@/components/onboarding/Step7Health";
import StepReady from "@/components/onboarding/StepReady";

const STEPS = [
  { component: Step1AboutYou, label: "Step 1 of 7 · About You" },
  { component: Step2Goal, label: "Step 2 of 7 · Your Goal" },
  { component: Step3Body, label: "Step 3 of 7 · Body" },
  { component: Step4Training, label: "Step 4 of 7 · Training" },
  { component: Step5Nutrition, label: "Step 5 of 7 · Nutrition" },
  { component: Step6Recovery, label: "Step 6 of 7 · Recovery" },
  { component: Step7Health, label: "Step 7 of 7 · Health" },
];

const INITIAL = {
  // 1
  age: "",
  height_unit: "imperial",
  height_feet: "",
  height_inches: "",
  height_cm: "",
  weight_unit: "lbs",
  current_weight: "",
  gender: "",
  // 2
  primary_goal: "",
  target_weight: "",
  target_body_fat: "",
  physique_goal: "",
  // 3
  knows_body_fat: "",
  body_fat_pct: "",
  waist: "",
  progress_photos: {},
  // 4
  training_days: 3,
  training_types: [],
  training_experience: "",
  activity_level: "",
  daily_steps: 6000,
  injuries: "",
  // 5
  food_tracking: "",
  daily_calories: "",
  daily_protein: "",
  daily_carbs: "",
  daily_fats: "",
  nutrition_struggles: [],
  dietary_restrictions: [],
  water_intake: "",
  // 6
  sleep_hours: 7,
  stress_level: 5,
  shift_work: "",
  energy_level: "",
  mood: "",
  // 7
  medications: "",
  hormone_therapy: "",
  dieted_recently: "",
  weight_change_6mo: "",
};

// 0 = welcome, 1..7 = step components, 8 = ready/finish
const TOTAL = 9;

// Persist mid-flow state so the user can close the tab and pick back up.
const STORAGE_KEY = "ta_onboarding_v1";

function loadDraft() {
  try {
    const raw = typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.step !== "number" || !parsed.data) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}
function saveDraft(step, data) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ step, data, ts: Date.now() }));
  } catch (_) { /* ignore */ }
}
function clearDraft() {
  try { window.localStorage.removeItem(STORAGE_KEY); } catch (_) { /* ignore */ }
}

export default function Onboarding() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const draft = loadDraft();
  // If there's a draft past welcome, sit on Welcome and offer a Resume button.
  // The user explicitly chooses Resume or Start-over.
  const initialResumeStep = draft && draft.step > 0 ? draft.step : null;
  const initialData = draft?.data && initialResumeStep ? { ...INITIAL, ...draft.data } : INITIAL;
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initialData);
  const [resumeStep, setResumeStep] = useState(initialResumeStep);
  const [saving, setSaving] = useState(false);

  // Whenever step or data changes, persist a draft so the user can resume.
  // Skip while saving the final form (effect re-runs as `saving` flips back
  // to false) and skip on Welcome / Ready screens.
  useEffect(() => {
    if (saving) return;
    if (step === 0) return;
    if (step > STEPS.length) return;  // 'Ready' screen — don't re-create the draft
    saveDraft(step, data);
  }, [step, data, saving]);

  const setField = useCallback(
    (patch) => setData((d) => ({ ...d, ...patch })),
    []
  );

  // Convert raw form data → fields we'll persist on the user document.
  function buildUserUpdate() {
    const height_inches =
      data.height_unit === "imperial"
        ? Number(data.height_feet) * 12 + Number(data.height_inches)
        : Math.round(Number(data.height_cm) / 2.54);

    const num = (v) => (v === "" || v === undefined ? null : Number(v));

    return {
      onboarding_complete: true,
      // 1
      age: num(data.age),
      height: height_inches,
      height_unit: data.height_unit,
      current_weight: num(data.current_weight),
      // Snapshot the weight at the time the plan was set so the dashboard can
      // show a maintenance-delta as the user's weight changes over time.
      baseline_weight: num(data.current_weight),
      baseline_set_at: new Date().toISOString(),
      weight_unit: data.weight_unit,
      gender: data.gender,
      // 2
      primary_goal: data.primary_goal,
      goal_weight: num(data.target_weight),
      target_body_fat: num(data.target_body_fat),
      physique_goal: data.physique_goal,
      // 3
      knows_body_fat: data.knows_body_fat,
      body_fat_pct: num(data.body_fat_pct),
      waist: num(data.waist),
      progress_photos: data.progress_photos,
      // 4
      training_days: data.training_days,
      training_types: data.training_types,
      training_experience: data.training_experience,
      activity_level: data.activity_level,
      daily_steps: data.daily_steps,
      injuries: data.injuries,
      // 5
      food_tracking: data.food_tracking,
      daily_calories: num(data.daily_calories),
      daily_protein: num(data.daily_protein),
      daily_carbs: num(data.daily_carbs),
      daily_fats: num(data.daily_fats),
      nutrition_struggles: data.nutrition_struggles,
      dietary_restrictions: data.dietary_restrictions,
      water_intake: data.water_intake,
      // 6
      sleep_hours: data.sleep_hours,
      stress_level: data.stress_level,
      shift_work: data.shift_work,
      energy_level: data.energy_level,
      mood: data.mood,
      // 7
      medications: data.medications,
      hormone_therapy: data.hormone_therapy,
      dieted_recently: data.dieted_recently,
      weight_change_6mo: data.weight_change_6mo,
    };
  }

  const handleFinish = async () => {
    setSaving(true);
    try {
      const update = buildUserUpdate();
      await base44.auth.updateMe(update);

      // Auto-create a FitnessGoal so the dashboard goal card lights up.
      if (update.current_weight && update.goal_weight) {
        const target = new Date();
        target.setMonth(target.getMonth() + 3);
        try {
          await base44.entities.FitnessGoal.create({
            goal_type: "weight",
            target_value: update.goal_weight,
            current_value: update.current_weight,
            unit: data.weight_unit || "lbs",
            target_date: target.toISOString().split("T")[0],
            is_active: true,
          });
        } catch (_) {
          /* non-fatal */
        }
      }
      if (refreshUser) await refreshUser();
      clearDraft();
      navigate("/");
    } finally {
      setSaving(false);
    }
  };

  // step 0 — welcome
  if (step === 0) {
    return (
      <OnboardingShell step={1} totalSteps={TOTAL} label="Welcome">
        <StepWelcome
          user={user}
          onNext={() => {
            // "Start a new plan" wipes any saved draft so the user truly starts fresh.
            if (resumeStep) {
              setData(INITIAL);
              clearDraft();
              setResumeStep(null);
            }
            setStep(1);
          }}
          resumeStep={resumeStep}
          onResume={resumeStep ? () => setStep(resumeStep) : undefined}
          onRestart={resumeStep ? () => {
            setData(INITIAL);
            clearDraft();
            setResumeStep(null);
          } : undefined}
        />
      </OnboardingShell>
    );
  }

  // step 8 — final / build plan
  if (step === STEPS.length + 1) {
    return (
      <OnboardingShell
        step={TOTAL}
        totalSteps={TOTAL}
        label="Ready"
        onBack={() => setStep(STEPS.length)}
      >
        <StepReady onFinish={handleFinish} saving={saving} />
      </OnboardingShell>
    );
  }

  // steps 1..7
  const idx = step - 1;
  const StepComp = STEPS[idx].component;
  return (
    <OnboardingShell
      step={step + 1}
      totalSteps={TOTAL}
      label={STEPS[idx].label}
      onBack={() => setStep(step - 1)}
    >
      <StepComp
        data={data}
        set={setField}
        onNext={() => setStep(step + 1)}
        saving={saving}
      />
    </OnboardingShell>
  );
}
